import { Button } from "components/ui/button";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import Bugsnag from "@bugsnag/js";
import { useSubscriptionBannerState } from "./use-subscription-banner-state";
import { goToManagePlan } from "utils/manage-plan";
import { loadChargebeeScript } from "utils/chargebee";
import { toError } from "utils/to-error";

export function SubscriptionBanner() {
  const user = useSelector(getUser);
  const queryClient = useQueryClient();
  const {
    shouldShowBanner,
    bannerType,
    isDismissed,
    dismissBanner,
    daysUntilCancellation,
    cancellationDate,
    daysLeftInTrial,
    trialEndDate,
    hasPaymentMethod,
    isAdmin,
    adminEmail,
    gateway,
  } = useSubscriptionBannerState(user);

  if (!shouldShowBanner || isDismissed || !bannerType) {
    return null;
  }

  // Inline Chargebee hosted-page flow for admins to add a payment method
  // without leaving the app. Used as a fallback when the server-rendered
  // /onboarding/manage-plan page can't complete the change — e.g., existing
  // users whose stored card is invalid.
  const handleAddPayment = async (e: React.MouseEvent) => {
    e.preventDefault();
    let chargebeeLibrary: ChargebeeGlobal;

    try {
      chargebeeLibrary = await loadChargebeeScript();
    } catch {
      toast.error("Payment system unavailable. Please try again later.");
      window.location.href = "/dashboard/subscription";
      return;
    }

    chargebeeLibrary.tearDown();
    const instance = chargebeeLibrary.init({
      site: gateway,
    });

    instance.openCheckout({
      hostedPage: async () => {
        try {
          const response = await fetch("/profile/billing/changeCardHp");

          if (!response.ok) {
            throw new Error(
              `Failed to load payment form: ${response.status} ${response.statusText}`
            );
          }

          return await response.json();
        } catch (error) {
          console.error("Error loading Chargebee hosted page:", error);
          Bugsnag.notify(toError(error), (event) => {
            event.context = "SubscriptionBanner: load changeCardHp";
          });
          toast.error(
            "Unable to load payment form. Please try again or contact support."
          );
          throw error;
        }
      },
      success: async (hostedPageId: string) => {
        if (!hostedPageId || typeof hostedPageId !== "string") {
          console.error("Invalid hostedPageId received:", hostedPageId);
          Bugsnag.notify(
            new Error(
              `SubscriptionBanner: Chargebee returned invalid hostedPageId (${typeof hostedPageId})`
            ),
            (event) => {
              event.addMetadata("chargebee", { hostedPageId });
            }
          );
          toast.error(
            "Payment processing error. Please contact support if your payment was charged."
          );
          return;
        }

        try {
          const response = await fetch(
            "/profile/billing/acknowledgeHostedPage",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ pageId: hostedPageId }),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Failed to acknowledge payment: ${response.status} ${response.statusText}`
            );
          }

          toast.success("Payment method added successfully!");
          queryClient.invalidateQueries({ queryKey: ["initial-user"] });
          dismissBanner();
        } catch (error) {
          console.error("Error acknowledging payment:", error);
          Bugsnag.notify(toError(error), (event) => {
            event.context = "SubscriptionBanner: acknowledgeHostedPage";
            event.addMetadata("chargebee", { hostedPageId });
          });
          toast.error(
            "Payment method may have been added, but verification failed. Please refresh the page or contact support if issues persist."
          );
          // Don't dismiss banner on acknowledgment failure - user should verify
        }
      },
      close: () => {
        // User closed modal without completing
      },
    });
  };

  // Pending Cancellation Banner (unchanged)
  if (bannerType === "cancellation") {
    const subscription = user?.subscription?.subscription;
    const isInTrial = subscription?.status === "in_trial";

    return (
      <div className="sticky top-0 z-50 w-full bg-red-50 border-b-2 border-red-200 px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Icon + Message */}
          <div className="flex items-center gap-3 flex-1">
            <i className="fa-regular fa-circle-exclamation text-red-600 text-xl"></i>
            <div className="text-sm text-red-900">
              {isAdmin ? (
                <>
                  <span className="font-semibold">
                    Your {isInTrial ? "trial" : "subscription"} is set to cancel
                    {daysUntilCancellation !== null &&
                      daysUntilCancellation > 0 && (
                        <>
                          {" "}
                          in {daysUntilCancellation} day
                          {daysUntilCancellation !== 1 ? "s" : ""}
                        </>
                      )}
                    {daysUntilCancellation === 0 && <> today</>}
                    {cancellationDate &&
                      (daysUntilCancellation === 0 ? (
                        <> ({format(cancellationDate, "d MMMM")})</>
                      ) : (
                        <> on {format(cancellationDate, "d MMMM")}</>
                      ))}
                    .
                  </span>
                  <span className="block mt-1 text-neutral-700">
                    {isInTrial ? (
                      <>
                        Your trial will end and access to Sked will be
                        discontinued. If this was a mistake or you'd like to
                        continue,{" "}
                        <a
                          href="/onboarding/manage-plan"
                          className="text-red-600 hover:text-red-800 underline"
                        >
                          pick a plan
                        </a>{" "}
                        or contact our support team for assistance.
                      </>
                    ) : daysUntilCancellation === 0 ? (
                      <>
                        Your account will be cancelled today. Changed your mind?{" "}
                        <a
                          href="/onboarding/manage-plan"
                          className="text-red-600 hover:text-red-800 underline"
                        >
                          Manage your plan
                        </a>{" "}
                        or reach out to our support team.
                      </>
                    ) : (
                      <>
                        You'll maintain full access to Sked until then. After{" "}
                        {cancellationDate && format(cancellationDate, "d MMMM")}
                        , your account will be cancelled. Changed your mind?{" "}
                        <a
                          href="/onboarding/manage-plan"
                          className="text-red-600 hover:text-red-800 underline"
                        >
                          Manage your plan
                        </a>{" "}
                        or reach out to our support team.
                      </>
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold">
                    {isInTrial ? "Trial" : "Subscription"} pending cancellation
                  </span>
                  <span className="block mt-1 text-neutral-700">
                    This account is set to cancel
                    {cancellationDate &&
                      (daysUntilCancellation === 0 ? (
                        <> today ({format(cancellationDate, "d MMMM")})</>
                      ) : (
                        <> on {format(cancellationDate, "d MMMM")}</>
                      ))}
                    .{" "}
                    {isInTrial
                      ? "The trial will end"
                      : daysUntilCancellation === 0
                        ? "Access will be discontinued"
                        : "Access will continue until then"}{" "}
                    and the account will be discontinued. If this was a mistake,
                    please contact your account admin
                    {adminEmail && (
                      <span className="font-semibold"> ({adminEmail})</span>
                    )}{" "}
                    to update the subscription.
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Dismiss button */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={dismissBanner}
              className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-100 transition-colors"
              aria-label="Dismiss banner"
            >
              <i className="fa-regular fa-times text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reverse Trial Banner — full-featured trial messaging
  if (bannerType === "reverse-trial") {
    // Headline copy varies by days left; voice is honest and concrete.
    const headline = (() => {
      if (daysLeftInTrial === null) {
        return "You're on a free full-featured trial.";
      }
      if (daysLeftInTrial < 0) {
        return "Your trial has ended.";
      }
      if (daysLeftInTrial === 0) {
        return "Your trial ends today.";
      }
      if (daysLeftInTrial === 1) {
        return "Your trial ends tomorrow.";
      }
      return `${daysLeftInTrial} days left in your free full-featured trial.`;
    })();

    // Supporting sentence — reinforces value and points to the single action.
    const adminBody = (() => {
      if (daysLeftInTrial === null || daysLeftInTrial > 1) {
        return "Every feature is unlocked. Pick a plan when you're ready to keep everything you're using.";
      }
      if (daysLeftInTrial === 1) {
        return "Pick a plan now to keep everything you're using after your trial ends.";
      }
      // 0 or negative
      return "Pick a plan now to keep access to Sked.";
    })();

    // Non-admin headline mirrors the admin copy ladder but addresses the
    // team collectively, since only admins can pick a plan.
    const nonAdminHeadline = (() => {
      if (daysLeftInTrial === null) {
        return "Your team is on a free full-featured trial.";
      }
      if (daysLeftInTrial < 0) {
        return "Your team's trial has ended.";
      }
      if (daysLeftInTrial === 0) {
        return "Your team's trial ends today.";
      }
      const suffix = daysLeftInTrial !== 1 ? "s" : "";
      return `Your team is on a free full-featured trial — ${daysLeftInTrial} day${suffix} left.`;
    })();

    return (
      <div className="sticky top-0 z-50 w-full bg-primary-50 border-b-2 border-primary-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Icon + Message */}
          <div className="flex items-start sm:items-center gap-3 flex-1 w-full">
            <i className="fa-regular fa-sparkles text-primary-600 text-xl"></i>
            <div className="text-sm text-primary-900 w-full">
              {isAdmin ? (
                <>
                  <span className="font-semibold font-gotham">
                    {headline}
                    {trialEndDate && daysLeftInTrial !== null && (
                      <>
                        {" "}
                        <span className="font-normal text-primary-700">
                          ({format(trialEndDate, "d MMMM")})
                        </span>
                      </>
                    )}
                  </span>
                  <span className="block mt-1 font-inter text-primary-700">
                    {adminBody}
                  </span>
                  {/*
                    `?coupon=open` is handled by the server-rendered
                    `/onboarding/manage-plan` page in schedugram-server
                    (signup-onboarding app), not by this client. When the
                    param is present the manage-plan page opens its coupon
                    entry drawer automatically. Owner: schedugram-server
                    `apps/signup-onboarding` — update there if the contract
                    changes. SKE-3308.
                  */}
                  <a
                    href="/onboarding/manage-plan?coupon=open"
                    className="inline-block mt-1.5 font-inter text-xs text-primary-500 hover:text-primary-700 hover:underline"
                  >
                    Have a coupon code? Apply it when you pick your plan &rarr;
                  </a>
                </>
              ) : (
                <>
                  <span className="font-semibold font-gotham">
                    {nonAdminHeadline}
                  </span>
                  <span className="block mt-1 font-inter text-primary-700">
                    Ask your account admin
                    {adminEmail && (
                      <span className="font-semibold"> ({adminEmail})</span>
                    )}{" "}
                    to pick a plan to keep access.
                  </span>
                </>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-3 flex-shrink-0 w-full sm:w-auto justify-end">
            {isAdmin && !hasPaymentMethod && (
              <Button variant="outline" size="md" onClick={handleAddPayment}>
                <i className="fa-regular fa-credit-card"></i>
                Add payment method
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="blue-primary"
                size="md"
                onClick={() => goToManagePlan()}
              >
                Pick a plan
              </Button>
            )}

            {/* Dismiss button */}
            <button
              onClick={dismissBanner}
              className="text-primary-600 hover:text-primary-800 p-2 rounded-lg hover:bg-primary-100 transition-colors"
              aria-label="Dismiss banner"
            >
              <i className="fa-regular fa-times text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
